/**
 * Typed-id (Zillow region/school/school-district) helpers for the area
 * migration. Areas from /v2/area/* carry at most one typed id; these
 * helpers map an area-like object onto the request shapes that carry it:
 *
 * - V3 search: `filters.scope = { type, id }` (`HpSearchScopeDto`)
 * - V2 saved/recent-search filter bodies: `zillowRegionIds` /
 *   `zillowSchoolIds` / `zillowSchoolDistrictIds` (a single numeric id each)
 *
 * Resources may carry a typed scope without a legacy area id. Older area
 * responses still expose the flattened Zillow id fields.
 */
import { type HpSearchScopeDto, HpSearchScopeType } from '@zg-rentals/ts-hotpads-api-client';

export type AreaWithZillowIds = {
  id?: string;
  zillowRegionId?: number;
  zillowSchoolId?: number;
  zillowSchoolDistrictId?: number;
};

/** Typed fields for the V2 saved/recent-search `filter` bodies. */
export type ZillowIdFilterFields = {
  zillowRegionIds?: number;
  zillowSchoolIds?: number;
  zillowSchoolDistrictIds?: number;
};

/**
 * The area's typed scope, or `undefined` when it has no typed id (not yet
 * backfilled). At most one id is ever populated per area.
 */
export function toSearchScope(area: AreaWithZillowIds | null | undefined): HpSearchScopeDto | undefined {
  if (area?.zillowRegionId != null) {
    return { type: HpSearchScopeType.ZILLOW_REGION, id: area.zillowRegionId };
  }
  if (area?.zillowSchoolId != null) {
    return { type: HpSearchScopeType.ZILLOW_SCHOOL, id: area.zillowSchoolId };
  }
  if (area?.zillowSchoolDistrictId != null) {
    return { type: HpSearchScopeType.ZILLOW_SCHOOL_DISTRICT, id: area.zillowSchoolDistrictId };
  }
  return undefined;
}

export function getSearchScopeKey(scope: HpSearchScopeDto | undefined): string | undefined {
  return scope ? `${scope.type}:${scope.id}` : undefined;
}

/** The V2 saved/recent-search `filter` field that carries the typed id for a scope's type. */
export function zillowIdFilterField(scopeType: HpSearchScopeType): keyof ZillowIdFilterFields {
  switch (scopeType) {
    case HpSearchScopeType.ZILLOW_SCHOOL:
      return 'zillowSchoolIds';
    case HpSearchScopeType.ZILLOW_SCHOOL_DISTRICT:
      return 'zillowSchoolDistrictIds';
    default:
      return 'zillowRegionIds';
  }
}

/**
 * The single typed field matching the scope's type for a V2 saved/recent
 * search `filter`. Empty object when there is no scope, so callers can spread
 * it and stay byte-identical without one.
 */
export function toZillowIdFilterFields(scope: HpSearchScopeDto | undefined): ZillowIdFilterFields {
  if (!scope) {
    return {};
  }
  return { [zillowIdFilterField(scope.type)]: scope.id };
}
